import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Tab, Tabs, Alert, Spinner, Card, Table } from 'react-bootstrap';
import { FaUser, FaIdCard, FaCheckCircle, FaMobile, FaInfoCircle, FaDownload, FaGlobe } from 'react-icons/fa';
import api from '../services/api';

const AEPSOnboardingModal = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('onboarding');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');

  // Onboarding States
  const [onboardingData, setOnboardingData] = useState({
    merchantcode: '',
    callback_url: '',
    state_id: '',
    amount: ''
  });

  // E-KYC States
  const [ekycData, setEkycData] = useState({
    merchantcode: '',
    mobile: '',
    aadhar: '',
    pan: '',
    name: '',
    dob: '',
    address: '',
    pincode: '',
    city: '',
    state: ''
  });

  // Status Check States
  const [statusData, setStatusData] = useState({
    merchantcode: '',
    mobile: '',
    pipe: 'bank2'
  });

  // Callback States
  const [callbackData, setCallbackData] = useState({
    encrypted_data: ''
  });

  // Lists
  const [states, setStates] = useState([]);
  const [onboardingUrl, setOnboardingUrl] = useState('');
  const [statusResult, setStatusResult] = useState(null);
  const [decryptedData, setDecryptedData] = useState(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      setActiveTab('onboarding');
      setMessage('');
      setMessageType('info');
      setOnboardingData({
        merchantcode: '',
        callback_url: '',
        state_id: '',
        amount: ''
      });
      setEkycData({
        merchantcode: '',
        mobile: '',
        aadhar: '',
        pan: '',
        name: '',
        dob: '',
        address: '',
        pincode: '',
        city: '',
        state: ''
      });
      setStatusData({
        merchantcode: '',
        mobile: '',
        pipe: 'bank2'
      });
      setCallbackData({
        encrypted_data: ''
      });
      setStates([]);
      setOnboardingUrl('');
      setStatusResult(null);
      setDecryptedData(null);
      loadStates();
    }
  }, [show]);

  const showMessage = (msg, type = 'info') => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  // Load States
  const loadStates = async () => {
    try {
      const response = await api.get('/api/aeps-onboarding/state-list');
      if (response.data.success) {
        setStates(response.data.data);
      }
    } catch (error) {
      console.error('Load States Error:', error);
    }
  };

  // Generate Onboarding URL
  const generateOnboardingUrl = async () => {
    if (!onboardingData.merchantcode || !onboardingData.callback_url || !onboardingData.amount) {
      showMessage('Please fill all required fields', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/aeps-onboarding/generate-url', onboardingData);

      if (response.data.success) {
        setOnboardingUrl(response.data.data?.onboarding_url || '');
        showMessage('Onboarding URL generated successfully', 'success');
        if (onSuccess) {
          onSuccess(response.data.data);
        }
      } else {
        showMessage(response.data.message || 'Failed to generate onboarding URL', 'danger');
      }
    } catch (error) {
      console.error('Generate Onboarding URL Error:', error);
      showMessage('Failed to generate onboarding URL', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Merchant E-KYC
  const merchantEkyc = async () => {
    if (!ekycData.merchantcode || !ekycData.mobile || !ekycData.aadhar || !ekycData.pan || !ekycData.name || !ekycData.dob || !ekycData.address || !ekycData.pincode || !ekycData.city || !ekycData.state) {
      showMessage('Please fill all required fields', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/aeps-onboarding/merchant-ekyc', ekycData);

      if (response.data.success) {
        showMessage('Merchant E-KYC completed successfully', 'success');
        if (onSuccess) {
          onSuccess(response.data.data);
        }
      } else {
        showMessage(response.data.message || 'Merchant E-KYC failed', 'danger');
      }
    } catch (error) {
      console.error('Merchant E-KYC Error:', error);
      showMessage('Failed to complete merchant E-KYC', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Check Onboard Status
  const checkOnboardStatus = async () => {
    if (!statusData.merchantcode || !statusData.mobile) {
      showMessage('Please enter merchant code and mobile number', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/aeps-onboarding/status-check', {
        merchantcode: statusData.merchantcode,
        mobile: statusData.mobile,
        pipe: statusData.pipe || 'bank2'
      });

      if (response.data.success) {
        setStatusResult(response.data.data);
        const statusMsg = response.data.data?.is_approved === 'Accepted' 
          ? 'Onboarding completed successfully! Status: Accepted' 
          : `Onboard status: ${response.data.data?.is_approved || 'Pending'}`;
        showMessage(statusMsg, response.data.data?.is_approved === 'Accepted' ? 'success' : 'info');
      } else {
        showMessage(response.data.message || 'Failed to get onboard status', 'danger');
      }
    } catch (error) {
      console.error('Check Onboard Status Error:', error);
      showMessage('Failed to check onboard status', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Check CASA Onboard Status
  const checkCasaOnboardStatus = async () => {
    if (!statusData.merchantcode) {
      showMessage('Please enter merchant code', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/aeps-onboarding/status-check-casa', statusData);

      if (response.data.success) {
        setStatusResult(response.data.data);
        showMessage('CASA onboard status retrieved successfully', 'success');
      } else {
        showMessage(response.data.message || 'Failed to get CASA onboard status', 'danger');
      }
    } catch (error) {
      console.error('Check CASA Onboard Status Error:', error);
      showMessage('Failed to check CASA onboard status', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Get Mobile SDK Info
  const getMobileSdkInfo = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/aeps-onboarding/mobile-sdk');

      if (response.data.success) {
        showMessage('Mobile SDK information retrieved successfully', 'success');
        if (onSuccess) {
          onSuccess(response.data.data);
        }
      } else {
        showMessage(response.data.message || 'Failed to get mobile SDK information', 'danger');
      }
    } catch (error) {
      console.error('Get Mobile SDK Info Error:', error);
      showMessage('Failed to get mobile SDK information', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Decrypt Callback Data
  const decryptCallbackData = async () => {
    if (!callbackData.encrypted_data) {
      showMessage('Please enter encrypted data', 'danger');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/aeps-onboarding/decrypt-callback', callbackData);

      if (response.data.success) {
        setDecryptedData(response.data.data);
        showMessage('Callback data decrypted successfully', 'success');
      } else {
        showMessage(response.data.message || 'Failed to decrypt callback data', 'danger');
      }
    } catch (error) {
      console.error('Decrypt Callback Data Error:', error);
      showMessage('Failed to decrypt callback data', 'danger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaIdCard className="me-2" />
          AEPS Onboarding - Complete Onboarding System
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {message && (
          <Alert variant={messageType} className="mb-3">
            {message}
          </Alert>
        )}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-3"
        >
          {/* Onboarding Tab */}
          <Tab eventKey="onboarding" title="Onboarding">
            <div className="p-3">
              <h5><FaGlobe className="me-2" />Generate Onboarding URL</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Merchant Code *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter merchant code"
                      value={onboardingData.merchantcode}
                      onChange={(e) => setOnboardingData({...onboardingData, merchantcode: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Callback URL *</Form.Label>
                    <Form.Control
                      type="url"
                      placeholder="Enter callback URL"
                      value={onboardingData.callback_url}
                      onChange={(e) => setOnboardingData({...onboardingData, callback_url: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>State ID</Form.Label>
                    <Form.Select
                      value={onboardingData.state_id}
                      onChange={(e) => setOnboardingData({...onboardingData, state_id: e.target.value})}
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.name} ({state.code})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Amount *</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="Enter amount"
                      value={onboardingData.amount}
                      onChange={(e) => setOnboardingData({...onboardingData, amount: e.target.value})}
                      min="1"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button 
                variant="primary" 
                onClick={generateOnboardingUrl}
                disabled={loading}
                className="w-100 mb-3"
              >
                {loading ? <Spinner size="sm" /> : 'Generate Onboarding URL'}
              </Button>

              {onboardingUrl && (
                <Card className="mt-3">
                  <Card.Header>
                    <h6 className="mb-0">Generated Onboarding URL</h6>
                  </Card.Header>
                  <Card.Body>
                    <p className="text-break">{onboardingUrl}</p>
                    <Button 
                      variant="success" 
                      onClick={() => window.open(onboardingUrl, '_blank')}
                      className="me-2"
                    >
                      <FaGlobe className="me-1" />
                      Open URL
                    </Button>
                    <Button 
                      variant="info" 
                      onClick={() => navigator.clipboard.writeText(onboardingUrl)}
                    >
                      Copy URL
                    </Button>
                  </Card.Body>
                </Card>
              )}
            </div>
          </Tab>

          {/* E-KYC Tab */}
          <Tab eventKey="ekyc" title="E-KYC">
            <div className="p-3">
              <h5><FaUser className="me-2" />Merchant E-KYC</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Merchant Code *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter merchant code"
                      value={ekycData.merchantcode}
                      onChange={(e) => setEkycData({...ekycData, merchantcode: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mobile Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 10-digit mobile number"
                      value={ekycData.mobile}
                      onChange={(e) => setEkycData({...ekycData, mobile: e.target.value})}
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Aadhar Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 12-digit Aadhar number"
                      value={ekycData.aadhar}
                      onChange={(e) => setEkycData({...ekycData, aadhar: e.target.value})}
                      maxLength={12}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>PAN Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter PAN number"
                      value={ekycData.pan}
                      onChange={(e) => setEkycData({...ekycData, pan: e.target.value})}
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Name *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter full name"
                      value={ekycData.name}
                      onChange={(e) => setEkycData({...ekycData, name: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Date of Birth *</Form.Label>
                    <Form.Control
                      type="date"
                      value={ekycData.dob}
                      onChange={(e) => setEkycData({...ekycData, dob: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Address *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      placeholder="Enter complete address"
                      value={ekycData.address}
                      onChange={(e) => setEkycData({...ekycData, address: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Pincode *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 6-digit pincode"
                      value={ekycData.pincode}
                      onChange={(e) => setEkycData({...ekycData, pincode: e.target.value})}
                      maxLength={6}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>City *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter city"
                      value={ekycData.city}
                      onChange={(e) => setEkycData({...ekycData, city: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>State *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter state"
                      value={ekycData.state}
                      onChange={(e) => setEkycData({...ekycData, state: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button 
                variant="success" 
                onClick={merchantEkyc}
                disabled={loading}
                className="w-100"
              >
                {loading ? <Spinner size="sm" /> : 'Complete E-KYC'}
              </Button>
            </div>
          </Tab>

          {/* Status Check Tab */}
          <Tab eventKey="status" title="Status Check">
            <div className="p-3">
              <h5><FaCheckCircle className="me-2" />Onboard Status Check</h5>
              
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Merchant Code *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter merchant code (e.g., PS00210301)"
                      value={statusData.merchantcode}
                      onChange={(e) => setStatusData({...statusData, merchantcode: e.target.value})}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mobile Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter 10-digit mobile number"
                      value={statusData.mobile}
                      onChange={(e) => setStatusData({...statusData, mobile: e.target.value})}
                      maxLength={10}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Pipe *</Form.Label>
                    <Form.Select
                      value={statusData.pipe}
                      onChange={(e) => setStatusData({...statusData, pipe: e.target.value})}
                    >
                      <option value="bank2">Bank2</option>
                      <option value="bank3">Bank3</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6} className="d-flex align-items-end">
                  <Button 
                    variant="info" 
                    onClick={checkOnboardStatus}
                    disabled={loading}
                    className="w-100"
                  >
                    {loading ? <Spinner size="sm" /> : 'Check Status'}
                  </Button>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  <Button 
                    variant="warning" 
                    onClick={checkCasaOnboardStatus}
                    disabled={loading}
                    className="w-100"
                  >
                    {loading ? <Spinner size="sm" /> : 'Check CASA Status'}
                  </Button>
                </Col>
              </Row>

              {statusResult && (
                <Card className="mt-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Status Result</h6>
                    {statusResult.is_approved && (
                      <span className={`badge ${statusResult.is_approved === 'Accepted' ? 'bg-success' : statusResult.is_approved === 'KYC-Pending' ? 'bg-warning' : 'bg-danger'}`}>
                        {statusResult.is_approved === 'Accepted' ? '✅ Completed' : 
                         statusResult.is_approved === 'KYC-Pending' ? '⏳ Pending' : 
                         statusResult.is_approved}
                      </span>
                    )}
                  </Card.Header>
                  <Card.Body>
                    {statusResult.is_approved === 'Accepted' ? (
                      <Alert variant="success">
                        <FaCheckCircle className="me-2" />
                        <strong>Onboarding Completed Successfully!</strong>
                        <hr />
                        <div className="mt-2">
                          <p><strong>Status:</strong> {statusResult.is_approved}</p>
                          {statusResult.bank && (
                            <div>
                              <strong>Bank Status:</strong>
                              <ul className="mb-0 mt-2">
                                {Object.entries(statusResult.bank).map(([bank, status]) => (
                                  <li key={bank}>
                                    {bank}: {status === 1 ? '✅ Active' : '❌ Inactive'}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {statusResult.refno && <p><strong>Reference No:</strong> {statusResult.refno}</p>}
                          {statusResult.txnid && <p><strong>Transaction ID:</strong> {statusResult.txnid}</p>}
                        </div>
                      </Alert>
                    ) : (
                      <Alert variant={statusResult.is_approved === 'KYC-Pending' ? 'warning' : 'info'}>
                        <strong>Status:</strong> {statusResult.is_approved || 'Pending'}
                        {statusResult.message && <p className="mb-0 mt-2">{statusResult.message}</p>}
                      </Alert>
                    )}
                    <details className="mt-3">
                      <summary className="cursor-pointer">View Full Response</summary>
                      <pre className="mb-0 mt-2">{JSON.stringify(statusResult, null, 2)}</pre>
                    </details>
                  </Card.Body>
                </Card>
              )}
            </div>
          </Tab>

          {/* Mobile SDK Tab */}
          <Tab eventKey="mobile-sdk" title="Mobile SDK">
            <div className="p-3">
              <h5><FaMobile className="me-2" />Mobile SDK Information</h5>
              
              <Button 
                variant="info" 
                onClick={getMobileSdkInfo}
                disabled={loading}
                className="w-100 mb-3"
              >
                {loading ? <Spinner size="sm" /> : 'Get Mobile SDK Info'}
              </Button>

              <Alert variant="info">
                <FaInfoCircle className="me-2" />
                <strong>Mobile SDK Information:</strong>
                <ul className="mb-0 mt-2">
                  <li>SDK Version: 1.0.0</li>
                  <li>Supported Platforms: Android, iOS</li>
                  <li>Minimum Android Version: 5.0 (API 21)</li>
                  <li>Minimum iOS Version: 10.0</li>
                  <li>Features: E-KYC, Document Upload, Biometric Authentication</li>
                </ul>
              </Alert>

              <Card className="mt-3">
                <Card.Header>
                  <h6 className="mb-0">SDK Integration Steps</h6>
                </Card.Header>
                <Card.Body>
                  <ol>
                    <li>Download the mobile SDK from the provided link</li>
                    <li>Integrate the SDK into your mobile application</li>
                    <li>Configure the SDK with your merchant credentials</li>
                    <li>Implement the required callbacks and handlers</li>
                    <li>Test the integration in UAT environment</li>
                    <li>Deploy to production after successful testing</li>
                  </ol>
                </Card.Body>
              </Card>
            </div>
          </Tab>

          {/* Callback Tab */}
          <Tab eventKey="callback" title="Callback">
            <div className="p-3">
              <h5><FaInfoCircle className="me-2" />Callback Data Decryption</h5>
              
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Encrypted Data *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      placeholder="Enter encrypted callback data"
                      value={callbackData.encrypted_data}
                      onChange={(e) => setCallbackData({...callbackData, encrypted_data: e.target.value})}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button 
                variant="primary" 
                onClick={decryptCallbackData}
                disabled={loading}
                className="w-100 mb-3"
              >
                {loading ? <Spinner size="sm" /> : 'Decrypt Data'}
              </Button>

              {decryptedData && (
                <Card className="mt-3">
                  <Card.Header>
                    <h6 className="mb-0">Decrypted Callback Data</h6>
                  </Card.Header>
                  <Card.Body>
                    <pre className="mb-0">{JSON.stringify(decryptedData, null, 2)}</pre>
                  </Card.Body>
                </Card>
              )}

              <Alert variant="warning" className="mt-3">
                <FaInfoCircle className="me-2" />
                <strong>Callback URL Setup:</strong>
                <p className="mb-0 mt-2">
                  Set up your callback URL in the PaySprint portal to receive onboarding callbacks. 
                  The callback will contain encrypted data that needs to be decrypted using your JWT key.
                </p>
              </Alert>
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AEPSOnboardingModal;
